import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  FiSend,
  FiSquare,
  FiPaperclip,
  FiChevronDown,
  FiX,
  FiBook,
  FiZap,
  FiFileText,
  FiEdit3,
  FiMove,
  FiCornerUpLeft,
  FiArrowLeft,
} from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";
import ChatMarkdown from "./ChatMarkdown.jsx";
import { base64ToFile } from "../utils/fileStorage";
import styles from "../styles/miniChat.module.css";
import homeStyles from "../styles/home.module.css";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5001";

const MiniChat = ({
  panelId,
  onClose,
  onDragStart,
  onDragEnd,
  isDragging,
  isDropTarget,
  documents = [],
  onUploadDocument,
}) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState("");
  const [showFormatDropdown, setShowFormatDropdown] = useState(false);
  const [showDocDropdown, setShowDocDropdown] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [viewingDoc, setViewingDoc] = useState(null); // Document being viewed in panel
  const [pdfUrl, setPdfUrl] = useState(null); // Object URL for PDF viewer
  
  const chatContainerRef = useRef(null);
  const textareaRef = useRef(null);
  const abortControllerRef = useRef(null);
  const messagesEndRef = useRef(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Create PDF URL when viewing a document
  useEffect(() => {
    if (viewingDoc) {
      try {
        // Check if it's a PDF
        const isPdf = viewingDoc.name?.toLowerCase().endsWith('.pdf') ||
                      viewingDoc.type === 'application/pdf' ||
                      viewingDoc.content?.startsWith('JVBERi');
        
        if (isPdf && viewingDoc.content) {
          // Convert base64 to blob URL
          const file = base64ToFile(viewingDoc.content, viewingDoc.name, viewingDoc.type || 'application/pdf');
          const url = URL.createObjectURL(file);
          setPdfUrl(url);
        } else {
          setPdfUrl(null);
        }
      } catch (error) {
        console.error('Error creating PDF URL:', error);
        setPdfUrl(null);
      }
    } else {
      // Clean up URL when not viewing
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
        setPdfUrl(null);
      }
    }
    
    // Cleanup on unmount
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewingDoc]);

  // Auto-resize textarea
  const queueResize = useCallback((textarea) => {
    if (textarea) {
      textarea.style.height = "auto";
      textarea.style.height = Math.min(textarea.scrollHeight, 150) + "px";
    }
  }, []);

  // Handle sending message
  const handleSendMessage = async (e) => {
    e?.preventDefault();
    if (!input.trim() || isStreaming) return;

    const messageText = input.trim();
    const userMessage = {
      id: Date.now(),
      role: "user",
      content: messageText,
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setIsStreaming(true);

    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }

    try {
      abortControllerRef.current = new AbortController();

      // Build context from previous messages
      const context = messages.map((m) => ({
        role: m.role === "assistant" ? "assistant" : "user",
        content: m.content,
      }));

      // Add document context if selected
      let finalMessage = messageText;
      if (selectedDoc?.content) {
        finalMessage = `Context from document "${selectedDoc.name}":\n${selectedDoc.content.substring(0, 2000)}\n\nUser question: ${messageText}`;
      }

      const response = await fetch(`${API_BASE}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: finalMessage,
          context,
        }),
        signal: abortControllerRef.current.signal,
      });

      if (response.status === 429) {
        setMessages((prev) => [
          ...prev,
          {
            id: Date.now() + 1,
            role: "assistant",
            content: "Rate limit reached. Please try again in a moment.",
          },
        ]);
        return;
      }

      if (!response.ok) throw new Error("Failed to get response");

      const data = await response.json();
      let replyText = "";
      
      // Handle different response formats
      if (data.reply) {
        if (typeof data.reply === "object") {
          replyText = data.reply.text || data.reply.content || JSON.stringify(data.reply);
        } else {
          replyText = String(data.reply);
        }
      } else if (data.text) {
        replyText = data.text;
      } else if (data.content) {
        replyText = data.content;
      } else {
        replyText = "I received your message but couldn't generate a response.";
      }

      const assistantMessage = {
        id: Date.now() + 1,
        role: "assistant",
        content: replyText,
      };

      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Error sending message:", error);
        setMessages((prev) => [
          ...prev,
          {
            id: Date.now() + 1,
            role: "assistant",
            content: "Sorry, I encountered an error. Please try again.",
          },
        ]);
      }
    } finally {
      setIsStreaming(false);
      abortControllerRef.current = null;
    }
  };

  const handleStopStreaming = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handlePromptClick = (prompt) => {
    setInput(prompt);
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }, 0);
  };

  const handleSelectDocument = (doc) => {
    setSelectedDoc(doc);
    setShowDocDropdown(false);
  };

  const suggestions = [
    "Explain this concept simply",
    "Create study notes",
    "Quiz me on this topic",
    "Summarize the key points",
  ];

  return (
    <div
      className={`${styles.miniChat} ${isDragging ? styles.dragging : ""} ${
        isDropTarget ? styles.dropTarget : ""
      }`}
    >
      {/* Panel Header */}
      <div className={styles.panelHeader}>
        <div
          className={styles.dragHandle}
          draggable
          onDragStart={(e) => {
            e.dataTransfer.setData("text/plain", panelId);
            onDragStart?.(panelId);
          }}
          onDragEnd={() => onDragEnd?.()}
        >
          <FiMove size={14} />
          <span>Panel {panelId}</span>
        </div>

        {/* Document Dropdown */}
        <div className={styles.docDropdownContainer}>
          <button
            className={styles.docDropdownBtn}
            onClick={() => setShowDocDropdown(!showDocDropdown)}
          >
            <FiFileText size={14} />
            <span>{viewingDoc ? `Viewing: ${viewingDoc.name}` : selectedDoc ? `Context: ${selectedDoc.name}` : "Documents"}</span>
            <FiChevronDown
              size={12}
              className={showDocDropdown ? styles.rotated : ""}
            />
          </button>
          {showDocDropdown && (
            <div className={styles.docDropdownMenu}>
              {documents.length > 0 ? (
                <>
                  <div className={styles.dropdownLabel}>Your Documents</div>
                  {documents.map((doc, idx) => (
                    <div key={idx} className={styles.docItemGroup}>
                      <div className={styles.docItemName}>
                        <FiFileText size={14} />
                        <span>{doc.name}</span>
                      </div>
                      <div className={styles.docItemActions}>
                        <button
                          className={styles.docActionBtn}
                          onClick={() => {
                            setViewingDoc(doc);
                            setShowDocDropdown(false);
                          }}
                          title="View in panel"
                        >
                          View
                        </button>
                        <button
                          className={styles.docActionBtn}
                          onClick={() => handleSelectDocument(doc)}
                          title="Use as chat context"
                        >
                          Context
                        </button>
                      </div>
                    </div>
                  ))}
                  <div className={styles.dropdownDivider} />
                </>
              ) : (
                <div className={styles.noDocsMessage}>No documents uploaded</div>
              )}
              <button
                className={styles.docDropdownItem}
                onClick={() => {
                  setShowDocDropdown(false);
                  onUploadDocument?.();
                }}
              >
                <FiPaperclip size={14} />
                <span>Upload New Document</span>
              </button>
              {(selectedDoc || viewingDoc) && (
                <button
                  className={styles.docDropdownItem}
                  onClick={() => {
                    setSelectedDoc(null);
                    setViewingDoc(null);
                    setShowDocDropdown(false);
                  }}
                >
                  <FiX size={14} />
                  <span>Clear All</span>
                </button>
              )}
            </div>
          )}
        </div>

        <div className={styles.panelActions}>
          <button
            className={styles.panelActionBtn}
            onClick={() => onClose?.(panelId)}
            title="Close panel"
          >
            <FiX size={16} />
          </button>
        </div>
      </div>

      {/* Document Viewer Mode */}
      {viewingDoc ? (
        <div className={styles.documentViewer}>
          <div className={styles.documentHeader}>
            <button 
              className={styles.backToChat}
              onClick={() => setViewingDoc(null)}
            >
              <FiArrowLeft size={14} />
              Back to Chat
            </button>
            <span className={styles.docTitle}>{viewingDoc.name}</span>
            <button
              className={styles.useAsContextBtn}
              onClick={() => {
                setSelectedDoc(viewingDoc);
                setViewingDoc(null);
              }}
            >
              Use as Context
            </button>
          </div>
          <div className={styles.documentContent}>
            {/* PDF Viewer using PDF.js like SidePanel */}
            {pdfUrl ? (
              <iframe
                src={`/pdfjs/web/viewer.html?file=${encodeURIComponent(pdfUrl)}`}
                className={styles.pdfViewer}
                title="Document viewer"
              />
            ) : viewingDoc.extractedText ? (
              <pre className={styles.textContent}>{viewingDoc.extractedText}</pre>
            ) : viewingDoc.content && typeof viewingDoc.content === 'string' && !viewingDoc.content.startsWith('JVBERi') ? (
              <pre className={styles.textContent}>{viewingDoc.content}</pre>
            ) : (
              <div className={styles.pdfNotice}>
                <FiFileText size={48} />
                <h4>Loading Document...</h4>
                <p>Please wait while the document loads.</p>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Chat Content */
        <div className={styles.chatContent}>
          <div className={styles.chatMessages} ref={chatContainerRef}>
            {messages.length === 0 ? (
              <div className={styles.emptyState}>
                <h2 className={styles.chatHeading}>
                  <span className={styles.gradientText}>Ask Lumi Anything</span>
                </h2>
                {selectedDoc && (
                  <div className={styles.docContext}>
                    <FiFileText size={14} />
                    <span>Context: {selectedDoc.name}</span>
                  </div>
                )}
              </div>
            ) : (
              <>
                <AnimatePresence>
                  {messages.map((message) => (
                    <motion.div
                      key={message.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`${styles.messageWrapper} ${message.role === "user" ? homeStyles.userMessageWrapper : homeStyles.assistantMessageWrapper}`}
                      data-message-type={message.role}
                    >
                      {message.role === "user" ? (
                        <div className={homeStyles.userMessageContainer}>
                          <div className={homeStyles.userBubble}>{message.content}</div>
                          <FiCornerUpLeft
                            className={homeStyles.messageActionIcon}
                            onClick={() => setInput(message.content)}
                            title="Edit and resend"
                          />
                        </div>
                      ) : (
                        <div className={homeStyles.assistantMessageContainer}>
                          <div className={homeStyles.assistantBubble}>
                            <ChatMarkdown content={message.content} />
                          </div>
                        </div>
                      )}
                    </motion.div>
                  ))}
                </AnimatePresence>
                <div ref={messagesEndRef} />
              </>
            )}
            {isStreaming && messages[messages.length - 1]?.content === "" && (
              <div className={styles.streamingIndicator}>
                <span></span>
                <span></span>
                <span></span>
              </div>
            )}
          </div>

        {/* Chat Input - using homeStyles for consistency */}
        <div className={styles.chatInputArea}>
          <div className={homeStyles.chatInputBar}>
            <form onSubmit={handleSendMessage} className={homeStyles.chatForm}>
              <div className={homeStyles.inputWrapper}>
                <textarea
                  ref={textareaRef}
                  className={homeStyles.chatInputField}
                  placeholder={
                    selectedDoc
                      ? `Ask about ${selectedDoc.name}...`
                      : "Ask Lumi anything..."
                  }
                  value={input}
                  onChange={(e) => {
                    setInput(e.target.value);
                    queueResize(e.target);
                  }}
                  onKeyDown={handleKeyPress}
                  rows={1}
                />
                <button
                  type={isStreaming ? "button" : "submit"}
                  className={homeStyles.sendBtn}
                  disabled={!isStreaming && !input.trim()}
                  onClick={isStreaming ? handleStopStreaming : undefined}
                >
                  {isStreaming ? <FiSquare size={16} /> : <FiSend size={16} />}
                </button>
              </div>
            </form>
          </div>

          {/* Format and Quick Actions */}
          <div className={styles.inputOptions}>
            <div className={styles.formatDropdown}>
              <button
                type="button"
                className={styles.formatBtn}
                onClick={() => setShowFormatDropdown(!showFormatDropdown)}
              >
                Format{selectedFormat ? `: ${selectedFormat}` : ""}
                <FiChevronDown size={12} />
              </button>
              {showFormatDropdown && (
                <div className={styles.formatMenu}>
                  <button type="button" onClick={() => { setSelectedFormat("Quiz"); setShowFormatDropdown(false); }}>
                    <FiBook size={12} /> Quiz
                  </button>
                  <button type="button" onClick={() => { setSelectedFormat("Flashcards"); setShowFormatDropdown(false); }}>
                    <FiZap size={12} /> Flashcards
                  </button>
                  <button type="button" onClick={() => { setSelectedFormat("Summary"); setShowFormatDropdown(false); }}>
                    <FiFileText size={12} /> Summary
                  </button>
                  <button type="button" onClick={() => { setSelectedFormat("Study Notes"); setShowFormatDropdown(false); }}>
                    <FiEdit3 size={12} /> Study Notes
                  </button>
                  {selectedFormat && (
                    <button type="button" onClick={() => { setSelectedFormat(""); setShowFormatDropdown(false); }}>
                      <FiX size={12} /> Clear
                    </button>
                  )}
                </div>
              )}
            </div>
            <button
              type="button"
              className={styles.quickBtn}
              onClick={() => handlePromptClick("Generate a quiz")}
              title="Quick Quiz"
            >
              <FiZap size={14} />
            </button>
            <button
              type="button"
              className={styles.quickBtn}
              onClick={() => handlePromptClick("Summarize this")}
              title="Summarize"
            >
              <FiFileText size={14} />
            </button>
            <button
              type="button"
              className={styles.quickBtn}
              onClick={() => handlePromptClick("Help me study")}
              title="Study Helper"
            >
              <FiBook size={14} />
            </button>
          </div>

          {/* Suggestions when empty */}
          {messages.length === 0 && (
            <div className={styles.suggestions}>
              {suggestions.map((prompt, idx) => (
                <button
                  key={idx}
                  className={styles.suggestionChip}
                  onClick={() => handlePromptClick(prompt)}
                >
                  {prompt}
                </button>
              ))}
            </div>
          )}
        </div>
        </div>
      )}
    </div>
  );
};

export default MiniChat;